import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

interface WorkerPerformanceTableProps {
  workers: {
    worker_id: string;
    name: string;
    tasks_completed: number;
    active: boolean;
    rating_credit_card?: number;
    rating_pdf?: number;
    rating_sec?: number;
  }[];
  loading: boolean;
  onDeactivate: (ids: string[]) => Promise<void>;
}

export const WorkerPerformanceTable: React.FC<WorkerPerformanceTableProps> = ({
  workers,
  loading,
  onDeactivate,
}) => {
  const [selected, setSelected] = useState<string[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelected(workers.map(w => w.worker_id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id: string) => {
    const newSelected = selected.includes(id)
      ? selected.filter(s => s !== id)
      : [...selected, id];
    setSelected(newSelected);
  };

  const handleDeactivate = async () => {
    try {
      setIsDeactivating(true);
      setError(null);
      await onDeactivate(selected);
      setSelected([]);
      setConfirmOpen(false);
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to deactivate workers');
      console.error('Failed to deactivate workers:', error);
    } finally {
      setIsDeactivating(false);
    }
  };

  if (loading) {
    return <CircularProgress />;
  }

  return (
    <Box>
      {selected.length > 0 && (
        <Button
          variant="contained"
          color="error"
          onClick={() => setConfirmOpen(true)}
          sx={{ mb: 2 }}
        >
          Deactivate Selected
        </Button>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selected.length > 0 && selected.length < workers.length}
                  checked={selected.length === workers.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>Worker ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Tasks Completed</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Credit Card Rating</TableCell>
              <TableCell>PDF Rating</TableCell>
              <TableCell>SEC Rating</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {workers.map((worker) => (
              <TableRow
                key={worker.worker_id}
                selected={selected.includes(worker.worker_id)}
                hover
              >
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={selected.includes(worker.worker_id)}
                    onChange={() => handleSelect(worker.worker_id)}
                  />
                </TableCell>
                <TableCell>{worker.worker_id}</TableCell>
                <TableCell>{worker.name}</TableCell>
                <TableCell>{worker.tasks_completed}</TableCell>
                <TableCell>{worker.active ? 'Active' : 'Inactive'}</TableCell>
                <TableCell>
                  {worker.rating_credit_card !== undefined 
                    ? `${(worker.rating_credit_card * 100).toFixed(1)}%` 
                    : '-'}
                </TableCell>
                <TableCell>
                  {worker.rating_pdf !== undefined 
                    ? `${(worker.rating_pdf * 100).toFixed(1)}%` 
                    : '-'}
                </TableCell>
                <TableCell>
                  {worker.rating_sec !== undefined 
                    ? `${(worker.rating_sec * 100).toFixed(1)}%` 
                    : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Deactivation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to deactivate {selected.length} selected workers?
            {error && (
              <Box color="error.main" mt={2}>
                {error}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={isDeactivating}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeactivate} 
            color="error" 
            variant="contained"
            disabled={isDeactivating}
          >
            {isDeactivating ? <CircularProgress size={24} /> : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WorkerPerformanceTable;
